import type { TonoPastilla } from '@/shared/ui/Pastilla/Pastilla'
import type {
  EstadoCotizacion,
  EstadoOrdenServicio,
  EstadoOrdenTrabajo,
} from '@/lib/supabase/tipos-bd'

/**
 * Cómo se ve y cómo se llama cada estado de los tres documentos.
 *
 * Un solo sitio para toda la aplicación: la matriz, el detalle, el tablero y
 * la vista de campo tienen que decirle lo mismo al usuario. Si el nombre de un
 * estado se escribe en dos pantallas distintas, tarde o temprano dejan de
 * coincidir.
 *
 * El naranja ('compuerta') está reservado para los estados donde alguien tiene
 * que decidir para que el trabajo avance. Es la misma señal que el esquema usa
 * con el borde naranja.
 */

type Presentacion = {
  etiqueta: string
  tono: TonoPastilla
  /** Qué significa el estado, para el título emergente y las ayudas. */
  explicacion: string
}

// ─── Documento 1 · Orden de servicio ────────────────────────────────────────

export const ESTADOS_ORDEN_SERVICIO: Record<EstadoOrdenServicio, Presentacion> = {
  creada: {
    etiqueta: 'Creada',
    tono: 'neutro',
    explicacion: 'Borrador. Todavía se puede corregir antes de pedir el precio.',
  },
  pendiente_cotizar: {
    etiqueta: 'Pendiente de cotizar',
    tono: 'marca',
    explicacion: 'Ya está contada en los tableros, aunque todavía no valga nada.',
  },
  cotizada: {
    etiqueta: 'Cotizada',
    tono: 'enCurso',
    explicacion: 'Tiene una cotización asociada con su número de World Office.',
  },
  anulada: {
    etiqueta: 'Anulada',
    tono: 'detenido',
    explicacion: 'Se descartó sin llegar a ejecutarse.',
  },
}

// ─── Documento 2 · Cotización ───────────────────────────────────────────────

export const ESTADOS_COTIZACION: Record<EstadoCotizacion, Presentacion> = {
  /*
   * Faltaba en este catálogo desde siempre, y el tipo tampoco lo declaraba.
   * Una cotización en borrador se está armando aquí y World Office todavía no
   * la conoce: se puede descartar sin dejar rastro allá.
   */
  borrador: {
    etiqueta: 'Borrador',
    tono: 'neutro',
    explicacion: 'Se está armando. World Office todavía no la conoce.',
  },
  pendiente: {
    etiqueta: 'Pendiente',
    tono: 'neutro',
    explicacion: 'Se pidió el precio. Falta el número y el valor de World Office.',
  },
  cotizada: {
    etiqueta: 'Cotizada',
    tono: 'compuerta',
    explicacion: 'Esperando la decisión de Gerencia. Es una compuerta del recorrido.',
  },
  /*
   * LA APROBACIÓN DE GERENCIA YA NO ES LA ÚLTIMA PALABRA: quien decide si el
   * trabajo se hace y a qué precio es el cliente. Gerencia aprueba internamente
   * y se la envía; la cotización queda aquí, esperando que él acepte desde su
   * enlace. Solo entonces pasa a aprobada y nace la orden de trabajo.
   */
  enviada: {
    etiqueta: 'Con el cliente',
    tono: 'compuerta',
    explicacion: 'Aprobada por Gerencia y enviada al cliente. Esperando que él la acepte.',
  },
  aprobada: {
    etiqueta: 'Aprobada',
    tono: 'cerrado',
    explicacion: 'Aprobada. Su orden de trabajo ya nació.',
  },
  rechazada: {
    etiqueta: 'Rechazada',
    tono: 'detenido',
    explicacion: 'Rechazada con justificación. El trabajo vuelve a quedar sin precio.',
  },
  /*
   * NO ES LO MISMO QUE RECHAZADA, y por eso tiene entrada propia.
   *
   * Rechazada quiere decir que una persona vio el precio y dijo que no.
   * Fallida quiere decir que World Office no llegó a crear el documento: no
   * hay número, no hay precio y no decidió nadie. Ponerlas juntas haría creer
   * que a alguien no le gustó la cotización, cuando lo que pasó es que nunca
   * llegó a existir.
   */
  fallida: {
    etiqueta: 'No se creó',
    tono: 'detenido',
    explicacion:
      'World Office no la creó. El trabajo quedó sin precio y se puede volver a pedir.',
  },
}

// ─── Documento 3 · Orden de trabajo ─────────────────────────────────────────

export const ESTADOS_ORDEN_TRABAJO: Record<EstadoOrdenTrabajo, Presentacion> = {
  creada: {
    etiqueta: 'Creada',
    tono: 'neutro',
    explicacion: 'Nació de la aprobación. Falta programarla.',
  },
  programada: {
    etiqueta: 'Programada',
    tono: 'marca',
    explicacion: 'Tiene colaborador, horas asignadas y fecha de inicio.',
  },
  en_ejecucion: {
    etiqueta: 'En ejecución',
    tono: 'enCurso',
    explicacion: 'El operario está en campo. Se acumulan las horas hombre.',
  },
  en_precierre: {
    etiqueta: 'En precierre',
    tono: 'compuerta',
    explicacion: 'Espera la decisión del gerente operativo. Es una compuerta del recorrido.',
  },
  cerrada: {
    etiqueta: 'Cerrada',
    tono: 'cerrado',
    explicacion: 'El coordinador cerró el trabajo y el acta quedó pedida. Falta la firma del cliente.',
  },
  finalizada: {
    etiqueta: 'Finalizada',
    tono: 'cerrado',
    explicacion: 'El cliente firmó el acta. El recorrido terminó.',
  },
  anulada: {
    etiqueta: 'Anulada',
    tono: 'detenido',
    explicacion: 'Se detuvo antes de cerrarse.',
  },
}

/**
 * Estados que exigen una decisión de alguien.
 *
 * Los tableros los destacan: son donde el trabajo se queda quieto esperando a
 * una persona, y por lo tanto donde se pierde el tiempo que después aparece
 * como retraso.
 */
export const ESTADOS_DE_COMPUERTA = {
  // 'enviada' también es compuerta: la pelota está donde el cliente, y lo que
  // él se demore en responder no es retraso de MEGA.
  cotizacion: ['cotizada', 'enviada'] as EstadoCotizacion[],
  ordenTrabajo: ['en_precierre'] as EstadoOrdenTrabajo[],
}
